import { useState, useEffect } from 'react';
import {
  BlockStack,
  Box,
  Checkbox,
  ColorPicker,
  InlineStack,
  Popover,
  TextField,
  hsbToHex,
  rgbToHsb,
  type HSBColor,
} from '@shopify/polaris';

export interface ColorPickerFieldProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  value: string;
  onChange: (hex: string) => void;
  label?: string;
  helpText?: string;
}

export function ColorPickerField({
  enabled,
  onEnabledChange,
  value,
  onChange,
  label = 'حط background color خلف الصورة',
  helpText = 'مفيد لو الصورة فيها أجزاء شفافة (PNG transparent). الـ NSE هيدمج الصورة فوق اللون.',
}: ColorPickerFieldProps) {
  const [open, setOpen] = useState(false);
  const [hsb, setHsb] = useState<HSBColor>(() => hexToHsbSafe(value));

  useEffect(() => {
    setHsb(hexToHsbSafe(value));
  }, [value]);

  const handleHsbChange = (next: HSBColor) => {
    setHsb(next);
    onChange(hsbToHex(next).toUpperCase());
  };

  const handleHexChange = (raw: string) => {
    const cleaned = raw.trim().toUpperCase();
    const withHash = cleaned.startsWith('#') ? cleaned : `#${cleaned}`;
    onChange(withHash);
  };

  return (
    <BlockStack gap="200">
      <Checkbox
        label={label}
        checked={enabled}
        onChange={onEnabledChange}
        helpText={helpText}
      />
      {enabled ? (
        <InlineStack gap="200" blockAlign="center">
          <Popover
            active={open}
            onClose={() => setOpen(false)}
            preferredAlignment="left"
            activator={
              <button
                type="button"
                onClick={() => setOpen(!open)}
                aria-label="اختار اللون"
                style={{
                  width: 44,
                  height: 36,
                  border: '1px solid var(--p-color-border)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  padding: 0,
                  background: value,
                }}
              />
            }
          >
            <Box padding="300">
              <ColorPicker color={hsb} onChange={handleHsbChange} />
            </Box>
          </Popover>
          <Box minWidth="120px">
            <TextField
              label=""
              labelHidden
              autoComplete="off"
              value={value}
              onChange={handleHexChange}
              placeholder="#FFFFFF"
              maxLength={9}
            />
          </Box>
        </InlineStack>
      ) : null}
    </BlockStack>
  );
}

function hexToHsbSafe(hex: string): HSBColor {
  try {
    const clean = hex.replace('#', '');
    if (clean.length < 6) return { hue: 0, saturation: 0, brightness: 1 };
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return rgbToHsb({ red: r, green: g, blue: b });
  } catch {
    return { hue: 0, saturation: 0, brightness: 1 };
  }
}
